'use client'

import { useEffect, useRef, type ReactNode } from 'react'
import type { ListingMediaItem, ResizedMediaVariant } from '@/lib/feedbridge/types'
import {
  HeroCarousel,
  ImageGallery,
  type ImageGalleryHandle,
} from '@/components/listings/ImageGallery'
import {
  OPEN_GALLERY_EVENT,
  OPEN_VIDEO_EVENT,
  OPEN_FLOORPLAN_EVENT,
} from '@/components/property/OpenGalleryButton'

interface PropertyDetailPageProps {
  resizedMedia?: Record<string, ResizedMediaVariant[]>
  addressLabel: string
  images: ListingMediaItem[]
  floorplans: ListingMediaItem[]
  videoUrl?: string | null
  mapCoordinates?: { lat: number; lng: number } | null
  /** e.g. "For Sale" / "Sold" — badge flush top-left of the gallery band */
  statusLabel?: string | null
  /** URL slug — used to set view-transition-name so the listing card
   * image on the previous page morphs into this hero on navigation. */
  vtSlug?: string
  children: ReactNode
}

/**
 * Property-detail media band + body.
 *
 * Figma 4022:84575 order is: Header → Hero (address only, white) → `Frame 506`
 * full-bleed gallery carousel → `Frame 502` body. The address hero is rendered
 * by the server page above this component; this component owns the gallery band
 * and hosts the lightbox that the sidebar `Resources → Gallery` button opens.
 */
export function PropertyDetailPage({
  resizedMedia,
  addressLabel,
  images,
  floorplans,
  videoUrl,
  mapCoordinates,
  statusLabel,
  vtSlug,
  children,
}: PropertyDetailPageProps) {
  const galleryRef = useRef<ImageGalleryHandle>(null)

  // The sidebar "Gallery" resource button is rendered inside a server component,
  // so it signals through a window event rather than a callback prop.
  useEffect(() => {
    const open = () => galleryRef.current?.openLightbox(0)
    const openVideo = () => galleryRef.current?.openVideo()
    const openFloorplans = () => galleryRef.current?.openFloorplans()
    window.addEventListener(OPEN_GALLERY_EVENT, open)
    window.addEventListener(OPEN_VIDEO_EVENT, openVideo)
    window.addEventListener(OPEN_FLOORPLAN_EVENT, openFloorplans)
    return () => {
      window.removeEventListener(OPEN_GALLERY_EVENT, open)
      window.removeEventListener(OPEN_VIDEO_EVENT, openVideo)
      window.removeEventListener(OPEN_FLOORPLAN_EVENT, openFloorplans)
    }
  }, [])

  return (
    <>
      {/* Full-bleed gallery carousel — Figma Frame 506 (4022:93510) */}
      {images.length > 0 && (
        <div style={vtSlug ? { viewTransitionName: `listing-${vtSlug}` } : undefined}>
          <HeroCarousel
            images={images}
            resizedMedia={resizedMedia}
            addressLabel={addressLabel}
            statusLabel={statusLabel}
            onOpen={() => galleryRef.current?.openLightbox(0)}
          />
        </div>
      )}

      {children}

      {/* Lightbox only — Figma has no tabbed/mosaic gallery section. */}
      <ImageGallery
        ref={galleryRef}
        images={images}
        floorplans={floorplans}
        resizedMedia={resizedMedia}
        videoUrl={videoUrl}
        addressLabel={addressLabel}
        mapCoordinates={mapCoordinates}
        showBrowser={false}
      />
    </>
  )
}
